import { navigateTo } from '@Actions/navigate.action';
import * as stationActions from '@Actions/stations.action';
import { error, success } from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	deleteLiftStationService,
	getImagesFromStationService,
	getLiftStationsService,
	getSensorGroupsFromStationService,
	getSensorsFromStationService,
	getSingleLiftStationService,
} from '@Services/liftStations';
import { getSensor } from '@Services/sensors';
import { ns } from '@Translations/translations';
import { MAIN_ROUTES } from '@Types/routes';
import { GetSensorsResponse } from '@Types/sensors.types';
import {
	LiftStation,
	ListStationsParams,
	PaginatedSensorGroupsFromStation,
	PaginatedSensorsFromStation,
	PaginatedStations,
	StationImagesResponse,
} from '@Types/stations.types';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { put, retry, takeLatest } from 'redux-saga/effects';

function* getStationsSaga(action: PayloadAction<ListStationsParams>) {
	const { getStationsResolved, getStationsRejected } = stationActions;

	try {
		const response: AxiosResponse<PaginatedStations> = yield retry(
			3,
			1500,
			getLiftStationsService,
			action.payload,
		);
		if (response.data) {
			yield put(getStationsResolved(response.data));
		} else {
			yield put(getStationsRejected());
			yield put(error(t('Failed to get facility', { ns })));
		}
	} catch (e) {
		yield put(getStationsRejected());
		yield put(error(t('Failed to get facility', { ns })));
	}
}

function* getFirstLocationFromSensorsSaga(action: PayloadAction<string[]>) {
	const {
		getFirstLocationFromSensorsResolved,
		getFirstLocationFromSensorsRejected,
	} = stationActions;
	try {
		const sensors = action.payload;
		for (const id of sensors) {
			const response: AxiosResponse<GetSensorsResponse> = yield retry(
				3,
				1500,
				getSensor,
				{ id },
			);
			if (response.data.sensor?.geometry?.coordinates.length) {
				yield put(
					getFirstLocationFromSensorsResolved(
						response.data.sensor.geometry.coordinates as [
							number,
							number,
						],
					),
				);
				return;
			}
		}
	} catch (e) {
		yield put(stationActions.getFirstLocationFromSensorsRejected());
	}
	yield put(getFirstLocationFromSensorsRejected());
}

export function* getSingleStationByIdSaga(action: PayloadAction<string>) {
	const { getSingleStationByIdResolved, getSingleStationByIdRejected } =
		stationActions;
	try {
		const response: AxiosResponse<LiftStation> = yield retry(
			3,
			1500,
			getSingleLiftStationService,
			action.payload,
		);
		if (response.data) {
			yield put(getSingleStationByIdResolved(response.data));
		} else {
			yield put(getSingleStationByIdRejected());
			yield put(error(t('Failed to get single facility', { ns })));
		}
	} catch (e) {
		yield put(getSingleStationByIdRejected());
		yield put(error(t('Failed to get single facility', { ns })));
	}
}

function* getSensorsFromStationSaga(action: PayloadAction<string>) {
	const { getSensorsFromStationResolved, getSensorsFromStationRejected } =
		stationActions;
	try {
		const response: AxiosResponse<PaginatedSensorsFromStation> =
			yield retry(3, 1500, getSensorsFromStationService, action.payload);
		if (response.data) {
			yield put(getSensorsFromStationResolved(response.data));
		} else {
			yield put(getSensorsFromStationRejected());
			yield put(error(t('Failed to get sensors', { ns })));
		}
	} catch (e) {
		yield put(getSensorsFromStationRejected());
		yield put(error(t('Failed to get sensors', { ns })));
	}
}

function* getSensorGroupsFromStationSaga(action: PayloadAction<string>) {
	const {
		getSensorGroupsFromStationResolved,
		getSensorGroupsFromStationRejected,
	} = stationActions;
	try {
		const response: AxiosResponse<PaginatedSensorGroupsFromStation> =
			yield retry(
				3,
				1500,
				getSensorGroupsFromStationService,
				action.payload,
			);
		if (response.data) {
			yield put(getSensorGroupsFromStationResolved(response.data));
		} else {
			yield put(getSensorGroupsFromStationRejected());
			yield put(error(t('Failed to get sensor groups', { ns })));
		}
	} catch (e) {
		yield put(getSensorGroupsFromStationRejected());
		yield put(error(t('Failed to get sensor groups', { ns })));
	}
}

function* getImagesFromStationSaga(action: PayloadAction<string>) {
	const { getImagesFromStationResolved, getImagesFromStationRejected } =
		stationActions;
	try {
		const response: AxiosResponse<StationImagesResponse> = yield retry(
			3,
			1500,
			getImagesFromStationService,
			action.payload,
		);
		if (response.data) {
			yield put(getImagesFromStationResolved(response.data.images ?? []));
		} else {
			yield put(getImagesFromStationRejected());
			yield put(error(t('Failed to get images', { ns })));
		}
	} catch (e) {
		yield put(getImagesFromStationRejected());
		yield put(error(t('Failed to get images', { ns })));
	}
}

function* deleteLiftStationSaga(action: PayloadAction<string>) {
	const { deleteLiftStationResolved, deleteLiftStationRejected } =
		stationActions;
	try {
		const response: AxiosResponse<{
			sensorConfigPairs: unknown[];
		}> = yield retry(1, 1500, deleteLiftStationService, action.payload);
		if (response.data) {
			yield put(deleteLiftStationResolved());
			yield put(success(t('Facility deleted successfully', { ns })));
			yield put(navigateTo(MAIN_ROUTES.Stations));
		} else {
			yield put(deleteLiftStationRejected());
			yield put(error(t('Failed to delete facility', { ns })));
		}
	} catch (e) {
		yield put(deleteLiftStationRejected());
		yield put(error(t('Failed to delete facility', { ns })));
	}
}

function* watchGetStations() {
	yield takeLatest(stationActions.getStations, getStationsSaga);
}

function* watchGetFirstLocationFromSensors() {
	yield takeLatest(
		stationActions.getFirstLocationFromSensors,
		getFirstLocationFromSensorsSaga,
	);
}

function* watchGetSingleStationById() {
	yield takeLatest(
		stationActions.getSingleStationById,
		getSingleStationByIdSaga,
	);
}

function* watchGetSensorsFromStation() {
	yield takeLatest(
		stationActions.getSensorsFromStation,
		getSensorsFromStationSaga,
	);
}

function* watchGetSensorGroupsFromStation() {
	yield takeLatest(
		stationActions.getSensorGroupsFromStation,
		getSensorGroupsFromStationSaga,
	);
}

function* watchGetImagesFromStation() {
	yield takeLatest(
		stationActions.getImagesFromStation,
		getImagesFromStationSaga,
	);
}

function* watchDeleteLiftStation() {
	yield takeLatest(stationActions.deleteLiftStation, deleteLiftStationSaga);
}

export default [
	watchGetStations(),
	watchGetFirstLocationFromSensors(),
	watchGetSingleStationById(),
	watchGetSensorsFromStation(),
	watchGetSensorGroupsFromStation(),
	watchGetImagesFromStation(),
	watchDeleteLiftStation(),
];
